import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgentReasonPopover } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-reason-popover';
import { Cell, flexRender, Row } from '@tanstack/react-table';
import { createStyles } from '@/contexts/theme-context';
import {
  getCellAlignmentStyle,
  usePricingCellAlignmentStyles,
} from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-alignment-styles';
import {
  isDeltaAgentColumn,
  isInputAgentColumn,
  isLeftAlignedAgentColumn,
  PricingRecommendationRow,
  RecommendationGroup,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import { Platform, Pressable, View } from 'react-native';
import {
  RecommendationAction,
  RejectionReason,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { resolveEffectiveAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/effective-recommendation-action';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';
import { useHover } from 'react-native-web-hooks';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';

type AgentReviewRow = RecommendationGroup | PricingRecommendationRow;

interface Props {
  defaultAccepted: boolean;
  disabled: boolean;
  index: number;
  row: Row<AgentReviewRow>;
}

const isRecommendationRow = (
  row: AgentReviewRow,
): row is PricingRecommendationRow => !('subRows' in row);

export const AgentTableRow = React.memo<Props>(
  ({ defaultAccepted, disabled, index, row }) => {
    const rowRef = useRef<React.ComponentRef<typeof Pressable>>(null);
    const isHovered = useHover(rowRef);
    const [isReasonOpen, setIsReasonOpen] = useState(false);
    const [styles] = useStyles();
    const [alignmentStyles] = usePricingCellAlignmentStyles();
    const suppressPressRef = useRef(false);

    const original = row.original;
    const recommendationRow = isRecommendationRow(original) ? original : null;
    const recommendationUUID = recommendationRow?.uuid ?? null;

    const entry = useRecommendationActionsStore((state) =>
      recommendationUUID ? state.actions[recommendationUUID] : undefined,
    );
    const cycleAction = useRecommendationActionsStore(
      (state) => state.cycleAction,
    );
    const rejectRecommendation = useRecommendationActionsStore(
      (state) => state.rejectRecommendation,
    );
    const acceptRecommendation = useRecommendationActionsStore(
      (state) => state.acceptRecommendation,
    );
    const setRejectionReason = useRecommendationActionsStore(
      (state) => state.setRejectionReason,
    );

    const action = resolveEffectiveAction(
      entry?.action,
      recommendationRow?.reviewStatus,
      defaultAccepted,
    );
    const isOverridden = action === RecommendationAction.Overridden;
    const isRejected = action === RecommendationAction.Rejected;
    const prevIsRejectedRef = useRef(isRejected);

    useEffect(() => {
      if (isRejected && !prevIsRejectedRef.current) {
        setIsReasonOpen(true);
      }

      if (!isRejected) {
        setIsReasonOpen(false);
      }

      prevIsRejectedRef.current = isRejected;
    }, [isRejected]);

    // Block the browser's default word-select on double-click so rapid clicks
    // that cycle the action don't accidentally highlight cell text. Drag-select
    // still works normally. Pressable's RN types don't expose onMouseDown, so
    // attach the listener directly to the DOM node via its ref.
    useEffect(() => {
      const node = rowRef.current as unknown as HTMLElement | null;
      if (!node || Platform.OS !== 'web') {
        return;
      }
      const handleMouseDown = (event: MouseEvent) => {
        if (event.detail >= 2) {
          event.preventDefault();
        }
        // Selecting text in a cell input and releasing outside it fires click
        // on the row (the common ancestor), bypassing the input's own
        // stopPropagation. Remember where the press started and ignore it.
        const target = event.target as Element | null;
        suppressPressRef.current = Boolean(
          target?.closest?.('input, textarea, [contenteditable="true"]'),
        );
      };
      node.addEventListener('mousedown', handleMouseDown);
      return () => {
        node.removeEventListener('mousedown', handleMouseDown);
      };
    }, []);

    const handlePress = useCallback(() => {
      if (suppressPressRef.current) {
        suppressPressRef.current = false;
        return;
      }
      if (!recommendationRow || isOverridden || disabled) {
        return;
      }
      if (!defaultAccepted) {
        cycleAction(
          recommendationRow.uuid,
          recommendationRow.restaurantUUID,
          recommendationRow.variantPackUUID,
          recommendationRow.supplierUUID,
        );
        return;
      }
      // Auto-accept toggles accepted ↔ rejected off the effective status, so a
      // row that is rejected on the backend un-rejects on the first click.
      const applyAction = isRejected
        ? acceptRecommendation
        : rejectRecommendation;
      applyAction(
        recommendationRow.uuid,
        recommendationRow.restaurantUUID,
        recommendationRow.variantPackUUID,
        recommendationRow.supplierUUID,
      );
    }, [
      acceptRecommendation,
      cycleAction,
      defaultAccepted,
      disabled,
      isOverridden,
      isRejected,
      recommendationRow,
      rejectRecommendation,
    ]);

    const handleReasonChange = useCallback(
      (reason: RejectionReason) => {
        if (recommendationUUID) {
          setRejectionReason(recommendationUUID, reason);
        }
      },
      [recommendationUUID, setRejectionReason],
    );

    const hoveredStyle = isHovered ? styles.hovered : undefined;

    const actionStyle = isOverridden
      ? styles.overridden
      : action === RecommendationAction.Accepted
        ? styles.accepted
        : action === RecommendationAction.Rejected
          ? styles.rejected
          : undefined;

    return (
      <Pressable
        onPress={handlePress}
        ref={rowRef}
        style={styles.rowWrapper}
        tabIndex={-1}
        testID={`agent-table-row-${row.index}`}
      >
        <TableRow index={index} key={row.id}>
          {row.getVisibleCells().map((cell: Cell<AgentReviewRow, unknown>) => {
            const columnId = cell.column.id;
            const alignmentStyle = getCellAlignmentStyle({
              isDelta: isDeltaAgentColumn(columnId),
              isLeftAligned: isLeftAlignedAgentColumn(columnId),
              styles: alignmentStyles,
            });
            const topAlignedStyle = isInputAgentColumn(columnId)
              ? styles.topAlignedInput
              : styles.topAligned;
            return (
              <TableCell
                cell={cell}
                key={cell.id}
                style={{
                  ...topAlignedStyle,
                  ...alignmentStyle,
                  ...hoveredStyle,
                  ...actionStyle,
                }}
              >
                {flexRender(cell.column.columnDef.cell, {
                  ...cell.getContext(),
                  index,
                })}
              </TableCell>
            );
          })}
        </TableRow>
        {recommendationUUID && isRejected && !disabled ? (
          <AgentReasonPopover
            initialReason={entry?.rejectionReason}
            isOpen={isReasonOpen}
            onOpenChange={setIsReasonOpen}
            onReasonChange={handleReasonChange}
            trigger={<View collapsable={false} style={styles.anchor} />}
          />
        ) : null}
      </Pressable>
    );
  },
);

const useStyles = createStyles((theme) => ({
  accepted: {
    backgroundColor: theme.sk.color.bg.success,
  },
  anchor: {
    // Align with where the item name text starts inside the pinned Name cell:
    // Table cellPadding (spaceMedium) + itemNameWrapper paddingLeft (spaceLarge).
    bottom: 0,
    height: 1,
    left: theme.sk.spacing.spaceMedium + theme.sk.spacing.spaceLarge,
    position: 'absolute',
    width: 1,
  },
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  overridden: {
    backgroundColor: theme.sk.color.bg.blue,
  },
  rejected: {
    backgroundColor: theme.sk.color.bg.error,
  },
  rowWrapper: {
    position: 'relative',
  },
  topAligned: {
    justifyContent: 'flex-start',
    paddingTop: 14,
  },
  topAlignedInput: {
    justifyContent: 'flex-start',
    // Compensate for the 32px input box: shrink the cell's top padding so the
    // input's value baseline aligns with the surrounding non-input text.
    paddingTop: 8,
  },
}));
